import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API_BASE = `${BACKEND_URL}/api`;
const BACKOFF_MS = [1000, 2000, 5000, 10000];
const MAX_ATTEMPTS_PER_SOURCE = 4;
const DEMOTE_WINDOW_MS = 90000;
const SESSION_ID = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now());
const IS_MOBILE = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
const FAIL_MESSAGE = "This station's stream is unavailable right now — try again shortly.";

export function usePlayer() {
  const [playingId, setPlayingId] = useState(null);
  const [station, setStation] = useState(null);
  const [status, setStatus] = useState("idle"); // idle|connecting|playing|reconnecting|failed|embed
  const [message, setMessage] = useState(null);
  const [embedUrl, setEmbedUrl] = useState(null);

  const audioRef = useRef(null);
  const stationRef = useRef(null);
  const sourcesRef = useRef([]);
  const srcIdxRef = useRef(0);
  const attemptRef = useRef(0);
  const demotedRef = useRef(new Set());
  const deathsRef = useRef({});
  const intentRef = useRef("paused"); // user intent: playing|paused
  const playStartRef = useRef(null);
  const startedRef = useRef(false);
  const reconnectingRef = useRef(false);
  const timerRef = useRef(null);
  const failureHandledRef = useRef(false); // dedupe: one failure per attempt
  const bufRef = useRef([]);

  // ── Part 0: telemetry (batched, fire-and-forget) ──────────────────────────
  const flush = useCallback((useBeacon = false) => {
    if (!bufRef.current.length) return;
    const events = bufRef.current.splice(0, 50);
    const payload = JSON.stringify({ events });
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(`${API_BASE}/telemetry/playback`,
        new Blob([payload], { type: "application/json" }));
    } else {
      axios.post(`${API_BASE}/telemetry/playback`, { events }).catch(() => {});
    }
  }, []);

  const logEvent = useCallback((eventType, mediaErrorCode = null) => {
    const st = stationRef.current;
    if (!st) return;
    const src = sourcesRef.current[srcIdxRef.current];
    bufRef.current.push({
      station_id: st.id,
      source_kind: src?.kind || "proxied",
      event_type: eventType,
      seconds_since_play_start: playStartRef.current
        ? Math.round((Date.now() - playStartRef.current) / 100) / 10 : null,
      media_error_code: mediaErrorCode,
      network_state: audioRef.current ? audioRef.current.networkState : null,
      user_agent_mobile: IS_MOBILE,
      screen_visibility_state: document.visibilityState,
      session_id: SESSION_ID,
    });
    if (bufRef.current.length >= 10) flush();
  }, [flush]);

  useEffect(() => {
    const t = setInterval(() => flush(), 10000);
    const onHide = () => flush(true);
    window.addEventListener("pagehide", onHide);
    return () => { clearInterval(t); window.removeEventListener("pagehide", onHide); flush(true); };
  }, [flush]);

  // ── Audio element (Part 3: live-radio attributes) ──────────────────────────
  const getAudio = useCallback(() => {
    if (!audioRef.current) {
      const a = new Audio();
      a.preload = "none"; // live radio: nothing to preload
      // no crossOrigin: direct station streams rarely send CORS headers
      a.addEventListener("playing", () => {
        setStatus("playing");
        setMessage(null);
        if (reconnectingRef.current) { logEvent("reconnect_success"); reconnectingRef.current = false; }
        attemptRef.current = 0;
        if (!startedRef.current) { startedRef.current = true; logEvent("play_start"); }
        if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "playing";
      });
      a.addEventListener("error", () => onFailureRef.current("error", a.error?.code ?? null));
      a.addEventListener("ended", () => {
        if (intentRef.current === "playing") onFailureRef.current("ended_unexpected", null);
      });
      a.addEventListener("stalled", () => { if (intentRef.current === "playing") logEvent("stalled"); });
      a.addEventListener("waiting", () => { if (intentRef.current === "playing") logEvent("waiting"); });
      audioRef.current = a;
    }
    return audioRef.current;
  }, [logEvent]);

  // ── Part 1/2: source selection + reconnect with backoff ───────────────────
  const setSource = useCallback((idx) => {
    const src = sourcesRef.current[idx];
    if (!src) return;
    srcIdxRef.current = idx;
    if (src.kind === "embed" || src.kind === "youtube_live") {
      // Embeds (station website) and youtube_live sources render as an iframe —
      // no <audio> failure signals available. The PlayerBar mounts the iframe
      // inline so playback continues across SPA route changes.
      getAudio().pause();
      setEmbedUrl(src.url);
      setStatus("playing");
      if (!startedRef.current) { startedRef.current = true; logEvent("play_start"); }
      return;
    }
    setEmbedUrl(null);
    const a = getAudio();
    const base = src.url.startsWith("/") ? `${BACKEND_URL}${src.url}` : src.url;
    failureHandledRef.current = false;
    a.src = `${base}${base.includes("?") ? "&" : "?"}cb=${Date.now()}`; // cache-bust
    a.load();
    a.play().catch(() => {
      if (intentRef.current === "playing") onFailureRef.current("error", a.error?.code ?? null);
    });
  }, [getAudio, logEvent]);

  const onFailureRef = useRef(() => {});
  onFailureRef.current = (eventType, mediaErrorCode) => {
    // Never reconnect against the user's intent (explicit pause), and never
    // loop while the tab is hidden with playback paused.
    if (intentRef.current !== "playing") return;
    // The same physical failure fires both the 'error' event and the play()
    // rejection — process only the first per attempt.
    if (failureHandledRef.current) return;
    failureHandledRef.current = true;
    logEvent(eventType, mediaErrorCode);
    const idx = srcIdxRef.current;
    // A source that dies twice within 90s is demoted for this session.
    const deaths = (deathsRef.current[idx] = deathsRef.current[idx] || []);
    deaths.push(Date.now());
    if (deaths.filter((t) => Date.now() - t < DEMOTE_WINDOW_MS).length >= 2) {
      demotedRef.current.add(idx);
    }
    clearTimeout(timerRef.current);
    // Demotion gates source SELECTION (never returning to a bad source) —
    // it must not truncate the in-place 1s/2s/5s/10s retry budget.
    const exhausted = attemptRef.current >= MAX_ATTEMPTS_PER_SOURCE;
    if (!exhausted) {
      const delay = BACKOFF_MS[Math.min(attemptRef.current, BACKOFF_MS.length - 1)];
      attemptRef.current += 1;
      reconnectingRef.current = true;
      setStatus("reconnecting");
      timerRef.current = setTimeout(() => {
        if (intentRef.current !== "playing") return;
        logEvent("reconnect_attempt");
        setSource(idx);
      }, delay);
      return;
    }
    // Fall through to the next non-demoted source, reset backoff.
    let next = idx + 1;
    while (next < sourcesRef.current.length && demotedRef.current.has(next)) next += 1;
    if (next < sourcesRef.current.length) {
      attemptRef.current = 0;
      reconnectingRef.current = true;
      setStatus("reconnecting");
      logEvent("source_fallthrough");
      setSource(next);
      return;
    }
    // All sources failed.
    logEvent("playback_failed");
    flush();
    intentRef.current = "paused";
    getAudio().pause();
    setStatus("failed");
    setMessage(FAIL_MESSAGE);
    setPlayingId(null);
    setEmbedUrl(null);
    if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "none";
  };

  // ── Part 3: MediaSession (lock-screen controls, background audio) ─────────
  const registerMediaSession = useCallback((station) => {
    if (!("mediaSession" in navigator)) return;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: station.name,
        artist: "Live · WaveScorePro",
        artwork: station.logo_url
          ? [{ src: station.logo_url, sizes: "512x512", type: "image/png" }] : [],
      });
      navigator.mediaSession.setActionHandler("play", () => resumeRef.current());
      // Live radio: "pause" is a full stop (no buffer to resume into).
      navigator.mediaSession.setActionHandler("pause", () => stopRef.current());
      navigator.mediaSession.setActionHandler("stop", () => stopRef.current());
      navigator.mediaSession.playbackState = "playing";
    } catch { /* older browsers */ }
  }, []);

  const play = useCallback(async (station) => {
    clearTimeout(timerRef.current);
    getAudio().pause();
    stationRef.current = station;
    setStation(station);
    intentRef.current = "playing";
    playStartRef.current = Date.now();
    startedRef.current = false;
    reconnectingRef.current = false;
    attemptRef.current = 0;
    demotedRef.current = new Set();
    deathsRef.current = {};
    setPlayingId(station.id);
    setStatus("connecting");
    setMessage(null);
    setEmbedUrl(null);
    let sources = [];
    let reason = null;
    try {
      const r = await axios.get(`${API_BASE}/public/playback-sources/${station.id}`);
      sources = r.data.sources || [];
      reason = r.data.reason || null;
    } catch { /* resolver unreachable — fall back to in-app proxy below */ }
    if (!sources.length && reason) {
      // FM-only station: no online stream — honest terminal state, no retries.
      setStatus("failed");
      setMessage(reason);
      setPlayingId(null);
      return;
    }
    if (!sources.length) {
      sources = [{ kind: "proxied", url: `/api/public/stream/${station.id}` }];
    }
    sourcesRef.current = sources;
    registerMediaSession(station);
    setSource(0);
  }, [getAudio, registerMediaSession, setSource]);

  const stop = useCallback(() => {
    intentRef.current = "paused";
    clearTimeout(timerRef.current);
    if (stationRef.current) logEvent("user_pause");
    getAudio().pause();
    setPlayingId(null);
    setStatus("idle");
    setEmbedUrl(null);
    if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "paused";
    flush();
  }, [getAudio, logEvent, flush]);

  const stopRef = useRef(stop);
  stopRef.current = stop;
  const resumeRef = useRef(() => {});
  resumeRef.current = () => { if (stationRef.current) play(stationRef.current); };

  const toggle = useCallback((station) => {
    if (playingId === station.id) stop();
    else play(station);
  }, [playingId, play, stop]);

  const setVolume = useCallback((v) => { getAudio().volume = Math.min(1, Math.max(0, v)); }, [getAudio]);

  useEffect(() => () => { clearTimeout(timerRef.current); audioRef.current?.pause(); }, []);

  return { playingId, station, status, message, embedUrl, toggle, stop, setVolume };
}
